import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import RadarChart from '../components/RadarChart';
import { fetcher } from '../services/api';

export default function PoliticianProfile() {
  const { id } = useParams();

  const { data, isLoading, error } = useSWR(`politicians/${id}`, fetcher);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200 rounded-xl mb-8" />
          <div className="h-32 bg-gray-200 rounded-xl mb-4" />
        </div>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">No encontrado</h1>
        <p className="text-gray-600">El político solicitado no existe.</p>
      </div>
    );
  }

  const p = data.data;

  const radarData = [
    { subject: 'Actividad', A: Math.min(p.stats?.initiatives * 5, 100), fullMark: 100 },
    { subject: 'Asistencia', A: p.attendance?.percentage || 0, fullMark: 100 },
    { subject: 'Aprobación', A: p.stats?.approved ? (p.stats.approved / p.stats.initiatives) * 100 : 0, fullMark: 100 },
    { subject: 'Impacto', A: p.rating || 50, fullMark: 100 },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Profile Header */}
      <div className="card mb-8">
        <div className="flex items-start space-x-6">
          {/* Photo */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 rounded-full bg-gray-200 overflow-hidden">
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{p.full_name}</h1>
                <p className="text-lg text-gray-600 mb-1">{p.position_title}</p>
                <div className="flex items-center space-x-2 mb-4">
                  <span
                    className="inline-block w-4 h-4 rounded-full"
                    style={{ backgroundColor: p.party_color || '#6B7280' }}
                  />
                  <span className="font-medium">{p.party_name}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-600">{p.state_name}</span>
                </div>
              </div>

              {/* Rating */}
              <div className="text-center">
                <div className={`text-4xl font-bold ${p.rating >= 80 ? 'text-green-600' : p.rating >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {p.rating}
                </div>
                <div className="text-sm text-gray-500">/ 100</div>
              </div>
            </div>

            {/* Contact */}
            {(p.contact_email || p.social_twitter) && (
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                {p.contact_email && (
                  <a href={`mailto:${p.contact_email}`} className="hover:text-primary-600">
                    📧 {p.contact_email}
                  </a>
                )}
                {p.social_twitter && (
                  <a href={`https://twitter.com/${p.social_twitter}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary-600">
                    🐦 @{p.social_twitter}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Stats */}
        <div className="lg:col-span-2 space-y-6">
          {/* Attendance */}
          <div className="card">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Asistencia Legislativa</h2>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-gray-900">{p.attendance?.total_sessions || 0}</p>
                <p className="text-sm text-gray-500">Total Sesiones</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-green-600">{p.attendance?.present || 0}</p>
                <p className="text-sm text-gray-500">Presentes</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-yellow-600">{p.attendance?.absent_justified || 0}</p>
                <p className="text-sm text-gray-500">Justificadas</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-red-600">{p.attendance?.absent_unjustified || 0}</p>
                <p className="text-sm text-gray-500">Injustificadas</p>
              </div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-green-500 h-4 rounded-full transition-all"
                  style={{ width: `${p.attendance?.percentage || 0}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {p.attendance?.percentage || 0}% de asistencia
              </p>
            </div>
          </div>

          {/* Initiatives */}
          <div className="card">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Actividad Legislativa</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-gray-900">{p.stats?.initiatives || 0}</p>
                <p className="text-sm text-gray-500">Iniciativas</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-green-600">{p.stats?.approved || 0}</p>
                <p className="text-sm text-gray-500">Aprobadas</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600">{p.stats?.committees || 0}</p>
                <p className="text-sm text-gray-500">Comisiones</p>
              </div>
            </div>
          </div>

          {/* Public Works */}
          {p.position_level === 'state' || p.position_level === 'municipal' ? (
            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Obras Públicas</h2>
              <p className="text-gray-600">Datos de obras en desarrollo...</p>
            </div>
          ) : null}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Radar Chart */}
          <div className="card">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Perfil de Desempeño</h2>
            <div className="flex justify-center">
              <RadarChart data={radarData} size={{ width: 200, height: 200 }} />
            </div>
          </div>

          {/* Strengths & Weaknesses */}
          <div className="card">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Fortalezas y Debilidades</h2>
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <span className="text-green-500">✓</span>
                <p className="text-sm text-gray-700">
                  {p.stats?.initiatives > 10 ? 'Alta productividad legislativa' : 'Actividad moderada'}
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-500">✓</span>
                <p className="text-sm text-gray-700">
                  {p.attendance?.percentage >= 90 ? 'Excelente asistencia' : p.attendance?.percentage >= 70 ? 'Buena asistencia' : 'Asistencia mejorable'}
                </p>
              </div>
              {p.attendance?.absent_unjustified > 5 && (
                <div className="flex items-start space-x-2">
                  <span className="text-red-500">✗</span>
                  <p className="text-sm text-gray-700">
                    {p.attendance.absent_unjustified} inasistencias injustificadas
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="card">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Acciones</h2>
            <div className="space-y-2">
              <button className="w-full btn btn-outline text-sm">
                Comparar con otro
              </button>
              <button className="w-full btn btn-outline text-sm">
                Suscribirse a alertas
              </button>
              <button className="w-full btn btn-outline text-sm">
                Compartir perfil
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
